import {Injectable} from "@angular/core";
import {FirebaseApp, initializeApp} from "firebase/app";
import {getMessaging, Messaging, getToken, onMessage, deleteToken} from "firebase/messaging";
import {environment} from "@src/environments/environment";
import {Platform} from "@angular/cdk/platform";
import {BehaviorSubject, defer, Observable, Subject} from "rxjs";
import {switchMap} from "rxjs/operators";
import {NotificationApiService} from "@app/services/api/notification-api.service";

@Injectable({
  providedIn: 'root'
})

export class FirebaseService {
  private firebaseApp: FirebaseApp | null = null;
  private firebaseMessaging: Messaging | null = null;
  private readonly FCM_TOKEN_KEY = 'fcm_device_token';
  private deviceTokenSubject = new BehaviorSubject<string | null>(null);
  private foregroundListenerInitialized = false;

  constructor(
    private platform: Platform,
    private notificationApiService: NotificationApiService
  ) {
  }

  /**
   * Get the FCM token from localStorage if it exists, otherwise return null
   * @private
   */
  private getLocalToken(): string | null {
    return localStorage.getItem(this.FCM_TOKEN_KEY);
  }

  /**
   * Save the FCM token to localStorage
   */
  private saveToken(token: string): void {
    localStorage.setItem(this.FCM_TOKEN_KEY, token);
  }

  /**
   * Clear the stored FCM token
   */
  private clearStoredToken(): void {
    localStorage.removeItem(this.FCM_TOKEN_KEY);
  }


  private initializeFirebase(): FirebaseApp {
    if (!this.firebaseApp) {
      this.firebaseApp = initializeApp(environment.firebase);
    }
    return this.firebaseApp;
  }

  private getFirebaseMessaging(): Messaging {
    if (!this.firebaseMessaging) {
      const app: FirebaseApp = this.initializeFirebase();
      this.firebaseMessaging = getMessaging(app);
    }
    return this.firebaseMessaging;
  }

  public getPlatform() {
    if (this.platform.ANDROID) {
      return 'android';
    } else if (this.platform.IOS) {
      return 'ios';
    } else {
      return 'web';
    }
  }

  /**
   * Setup foreground message listener
   * Listens for push notifications when the app is open/focused
   * Displays browser native notifications and updates badge immediately
   */
  public setupForegroundMessageListener(): void {
    if (this.foregroundListenerInitialized) {
      return;
    }

    const messaging = this.getFirebaseMessaging();

    onMessage(messaging, (payload) => {
      console.log('[FCM] Foreground message received:', payload);

      // Extract notification data
      const title = payload.notification?.title || payload.data?.notification_title || payload.data?.title || 'AutoBrief';
      const body = payload.notification?.body || payload.data?.notification_content || payload.data?.body || 'You have a new notification';
      const icon = payload.notification?.icon || '/assets/images/mobile/icon-192x192.png';
      const url = payload.data?.button_link || payload.data?.url || '/';

      // Check if notification permission is granted
      if (Notification.permission === 'granted') {
        // Create browser notification
        const notification = new Notification(title, {
          body: body,
          icon: icon,
          badge: '/assets/images/mobile/icon-96x96.png',
          tag: payload.data?.id || 'notification-' + Date.now(),
          requireInteraction: false,
          data: { url: url }
        });

        // Handle notification click
        notification.onclick = (event) => {
          event.preventDefault();
          window.focus();

          // Navigate to URL if provided
          if (url && url !== '/') {
            window.location.href = url;
          }

          notification.close();
        };

        // Reload notifications to update badge immediately
        this.notificationApiService.reloadNotifications();
      }
    });

    this.foregroundListenerInitialized = true;
  }

  /**
   * Get the existing service worker registration
   * The service worker is registered by Angular's ServiceWorkerModule (custom-sw.js which imports ngsw-worker.js)
   * Firebase uses the same registration for push notifications
   * @returns ServiceWorkerRegistration or null
   */
  private async getServiceWorkerRegistration(): Promise<ServiceWorkerRegistration | null> {
    try {
      if (!('serviceWorker' in navigator)) {
        return null;
      }

      // Wait for the service worker to be ready
      const registration = await navigator.serviceWorker.ready;
      return registration;
    } catch (error) {
      return null;
    }
  }

  /**
   * Generate or retrieve FCM token
   * @returns FCM token or null
   */
  public async generateToken(): Promise<string | null> {
    try {
      // Check current notification permission state
      const currentPermission = Notification.permission;

      if (currentPermission === 'denied') {
        return null;
      }

      // Check if we already have a valid token in localStorage
      const existingToken = this.getLocalToken();
      if (existingToken && this.deviceTokenSubject.value === existingToken) {
        return existingToken;
      }

      // Request notification permission only if not already granted
      let permission: "default" | "denied" | "granted" = currentPermission;
      if (currentPermission === 'default') {
        permission = await Notification.requestPermission();
        if (permission !== 'granted') {
          return null;
        }
      }

      const messaging: Messaging = this.getFirebaseMessaging();

      // Get the existing service worker registration (custom-sw.js which imports ngsw-worker.js)
      const serviceWorkerRegistration = await this.getServiceWorkerRegistration();

      if (!serviceWorkerRegistration) {
        return null;
      }

      const currentToken: string = await getToken(messaging, {
        vapidKey: environment.vapidKey,
        serviceWorkerRegistration: serviceWorkerRegistration
      });

      // Save token to localStorage and memory
      this.saveToken(currentToken);
      this.deviceTokenSubject.next(currentToken);

      return currentToken;
    } catch (error) {
      return null;
    }
  }

  /**
   * Subscribe to push notifications
   * Generates FCM token and registers device with backend
   * @param token - User authentication token (JWT or session token)
   * @returns Observable with a subscription result or null if token unchanged
   */
  public subscribe(token: string): Observable<any> {
    return defer(async () => {
      const newToken = await this.generateToken();
      const platform = this.getPlatform();

      if (newToken) {

        this.saveToken(newToken);
        this.deviceTokenSubject.next(newToken);

        return this.notificationApiService.subscribePushNotifications(
          platform,
          newToken
        );
      }

      // No token, return empty observable
      return new Observable(observer => {
        observer.complete();
      });
    }).pipe(
      switchMap((result) => result)
    );
  }

  /**
   * Unsubscribe from push notifications
   * Unregisters a device from backend
   * @returns Observable with an unsubscription result or null if no device token
   */
  public async unsubscribe() {
    try {
      const token: string = this.deviceTokenSubject.value;

      if (!token) {
        return;
      }

      await deleteToken(this.getFirebaseMessaging());

      this.notificationApiService.unsubscribePushNotifications(token).subscribe({
        error: (err) => {
          console.error('Failed to unsubscribe from push notifications:', err);
        }
      });

      this.clearStoredToken();
    } catch (error) {
      console.error('Error during FCM unsubscribe:', error);
      this.clearStoredToken();
    }
  }
}
